import { DefaultContext } from "@apollo/client";
import { useMemo } from "react";
import { useCurrentBrandData, useCurrentBrandId } from "utils/currentUserHooks";
import { AppMode, useAppMode } from "utils/appMode";
import { ADMIN_CONTEXT } from "./admin";
import { addActiveBrandIdToContext } from "./brand";

const EMPTY_CONTEXT = {};

type AdminOrBrandContextReturnTuple = [
  context: DefaultContext | undefined,
  isReady: boolean,
];

export function getAdminOrBrandContext(
  isAdmin: boolean | undefined,
  brandId: UUID | null | undefined,
  appMode: AppMode,
  context: DefaultContext = EMPTY_CONTEXT,
): AdminOrBrandContextReturnTuple {
  if (isAdmin) {
    return [{ ...context, ...ADMIN_CONTEXT }, true];
  }

  if (appMode === AppMode.BRAND) {
    return [addActiveBrandIdToContext(brandId, context), !!brandId];
  }

  return [context, true];
}

type Options = {
  context?: DefaultContext;
  isDisabledForParentBrand?: boolean;
};

export function useAdminOrBrandContext(
  isAdmin: boolean | undefined,
  { context = EMPTY_CONTEXT, isDisabledForParentBrand }: Options = {},
) {
  const brandId = useCurrentBrandId();

  const brandData = useCurrentBrandData();

  const appMode = useAppMode();

  const { id: brandDataId, isParent } = brandData || {};

  return useMemo<AdminOrBrandContextReturnTuple>(() => {
    if (!isAdmin && appMode === AppMode.BRAND) {
      if (!isDisabledForParentBrand && brandId !== brandDataId) {
        return [context, false];
      }

      if (isDisabledForParentBrand) {
        if (typeof isParent !== "boolean") return [context, false];
        if (isParent) return [context, true];
      }
    }

    return getAdminOrBrandContext(isAdmin, brandId, appMode, context);
  }, [
    isAdmin,
    isDisabledForParentBrand,
    isParent,
    brandId,
    brandDataId,
    appMode,
    context,
  ]);
}
